import React, { useState, useRef, useEffect } from 'react';
import { Send, Paperclip, FileText, ShieldCheck } from 'lucide-react';
import { api, API, timeAgo } from '../api';
import { toast } from 'sonner';

export function ChatThread({ messages, mySide, accent }) {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  return (
    <div className="space-y-1.5">
      {messages.map((m) => {
        const mine = m.sender === mySide;
        return (
          <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${m.id}`}>
            <div className={`max-w-[80%] rounded-xl px-2.5 py-1.5 text-xs ${mine ? `${accent} text-white rounded-br-sm` : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'}`}>
              {!mine && <p className="text-[9px] font-bold opacity-60 flex items-center gap-0.5">{m.sender === 'admin' && <ShieldCheck size={9} />}{m.sender_name || m.sender}</p>}
              {m.attachment?.kind === 'image' && (
                <a href={`${API}/files/${m.attachment.path}`} target="_blank" rel="noreferrer">
                  <img src={`${API}/files/${m.attachment.path}`} alt={m.attachment.name}
                    className="rounded-lg max-h-36 my-1 border border-black/10" />
                </a>
              )}
              {m.attachment?.kind === 'pdf' && (
                <a href={`${API}/files/${m.attachment.path}`} target="_blank" rel="noreferrer"
                  className={`flex items-center gap-1.5 my-1 px-2 py-1.5 rounded-lg text-[11px] font-bold ${mine ? 'bg-white/20' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  <FileText size={13} /> {m.attachment.name || 'document.pdf'}
                </a>
              )}
              {m.text && <p className="leading-snug whitespace-pre-wrap break-words">{m.text}</p>}
              <p className={`text-[8px] mt-0.5 ${mine ? 'text-white/60' : 'text-slate-400'}`}>{timeAgo(m.at)}</p>
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
}

export function ChatComposer({ onSend, accentBtn, idPrefix }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const send = async (attachment = null) => {
    if (!text.trim() && !attachment) return;
    setSending(true);
    try {
      await onSend(text.trim(), attachment);
      setText('');
    } finally {
      setSending(false);
    }
  };

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error('Max 10MB');
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/chat/upload', fd);
      await onSend(text.trim(), r.data);
      setText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex gap-1.5 items-center">
      <input type="file" ref={fileRef} onChange={pickFile} accept="image/jpeg,image/png,image/gif,image/webp,application/pdf" className="hidden" data-testid={`${idPrefix}-file-input`} />
      <button data-testid={`${idPrefix}-attach-button`} onClick={() => fileRef.current?.click()} disabled={sending}
        className="p-2 rounded-full bg-white border border-slate-300 text-slate-500 hover:text-slate-800 hover:border-slate-400 transition-colors disabled:opacity-50">
        <Paperclip size={13} />
      </button>
      <input
        data-testid={`${idPrefix}-text-input`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && send()}
        placeholder="Type a message…"
        className="flex-1 min-w-0 bg-white border border-slate-300 rounded-full px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-violet-400"
      />
      <button data-testid={`${idPrefix}-send-button`} onClick={() => send()} disabled={sending}
        className={`p-2 rounded-full text-white transition-colors disabled:opacity-50 ${accentBtn}`}>
        <Send size={13} />
      </button>
    </div>
  );
}

export default function ChatModule({ agent, messages, onSend, onRead }) {
  return (
    <div data-testid="chat-widget" className="flex flex-col" onClick={onRead}>
      <div className="max-h-[300px] min-h-[120px] overflow-y-auto scroll-thin pr-1 mb-2">
        {messages.length === 0
          ? <p className="font-hand text-lg text-purple-800/60 text-center py-6">No messages yet</p>
          : <ChatThread messages={messages} mySide="agent" accent="bg-purple-700" />}
      </div>
      <ChatComposer onSend={onSend} accentBtn="bg-purple-700 hover:bg-purple-800" idPrefix="agent-chat" />
    </div>
  );
}
